<template>
  <div
    class="dcb-logo-wall"
    aria-hidden="true"
  >
    <div
      v-for="(row, i) in rows"
      :key="i"
      class="dcb-logo-wall__row"
      :class="{ 'dcb-logo-wall__row--rev': row.reverse, 'dcb-logo-wall__row--slow': row.slow }"
    >
      <div class="dcb-logo-wall__track">
        <span
          v-for="j in 2"
          :key="j"
        >
          <span
            v-for="logo in row.logos"
            :key="logo.alt"
            class="dcb-logo-wall__tile"
          >
            <img
              v-if="logo.src"
              :src="logo.src"
              :alt="logo.alt"
              loading="lazy"
            />
          </span>
        </span>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
export type Logo = { src?: string; alt: string };
export type LogoWallRow = { logos: Logo[]; reverse?: boolean; slow?: boolean };

const props = defineProps<{
  rows?: LogoWallRow[];
}>();

const defaultRows: LogoWallRow[] = [
  {
    logos: [
      {
        src: '/content/content-images/dcb/messe/partner/54becf0ade1d949e0b42df8bd4f78381/app-store-icon.svg',
        alt: 'Apple App Store',
      },
      {
        src: '/content/content-images/dcb/messe/partner/3eb13bd4cbef9709059c9a6f639ca3a0/google-play.svg',
        alt: 'Google Play',
      },
      {
        src: '/content/content-images/dcb/messe/partner/22853014d8f873339a5a32aa7ce2620b/microsoft-store-xbox-logo.svg',
        alt: 'Microsoft Store',
      },
      {
        src: '/content/content-images/dcb/messe/partner/98da503852eee51f14e942448420ad0f/playstation-store-logo.svg',
        alt: 'PlayStation Store',
      },
    ],
  },
  {
    reverse: true,
    logos: [
      {
        src: '/content/content-images/dcb/messe/partner/00b1bcdacf4d730b05b9e1cbaaf43e7a/netflix-logo.svg',
        alt: 'Netflix',
      },
      {
        src: '/content/content-images/dcb/messe/partner/0e56026d9e356b3b3fad7f8e009678eb/logo-partner-spotify-premium.svg',
        alt: 'Spotify',
      },
      {
        src: '/content/content-images/dcb/messe/partner/cd04a0dbe71dc6b66f5976c634c87c13/deezer-logo.svg',
        alt: 'Deezer',
      },
      {
        src: '/content/content-images/dcb/messe/partner/e1d5462a579cef78e0dfd6294e215083/pay-by-phone.png',
        alt: 'PayByPhone',
      },
    ],
  },
  {
    slow: true,
    logos: [
      {
        src: '/content/content-images/dcb/messe/partner/ab27b3f99f1d1b9ed323d1bbd86f8f10/easypark-logo.svg',
        alt: 'easypark',
      },
      {
        src: '/content/content-images/dcb/messe/partner/e841542399ba1b8cf22878d48cb4643f/nextbike-logo.svg',
        alt: 'Nextbike',
      },
      {
        src: '/content/content-images/dcb/messe/partner/1a4e71baeeb272a51c6380fbaa1b8fd4/logo-partner-citywatt.svg',
        alt: 'CITYWATT',
      },
      {
        src: '/content/content-images/dcb/messe/partner/dffcac6eca3f179122f11b730b357322/logo-msu-solutions.svg',
        alt: 'MSU Solutions',
      },
    ],
  },
];

const rows = computed(() => props.rows ?? defaultRows);
</script>

<style scoped lang="scss">
@keyframes dcb-lwall-scroll {
  from {
    transform: translate3d(0, 0, 0);
  }
  to {
    transform: translate3d(-50%, 0, 0);
  }
}

.dcb-logo-wall {
  --lwall-gap: 12px;
  --lwall-tile-size: clamp(72px, 8vw, 96px);

  width: 100%;
  height: 100%;
  min-height: 240px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: var(--lwall-gap);
  padding-block: var(--lwall-gap);
  background-color: var(--bg-color);
  overflow: hidden;
  mask-image: linear-gradient(90deg, transparent 0%, #000 12%, #000 88%, transparent 100%);

  &__row {
    overflow-x: clip;
    overflow-y: visible;
  }

  &__track {
    display: flex;
    width: max-content;
    animation: dcb-lwall-scroll 40s linear infinite;
    will-change: transform;
  }

  &__row--rev &__track {
    animation-direction: reverse;
  }

  &__row--slow &__track {
    animation-duration: 50s;
  }

  &__tile {
    flex: 0 0 auto;
    width: var(--lwall-tile-size);
    height: var(--lwall-tile-size);
    margin-inline-end: var(--lwall-gap);
    display: inline-flex;
    align-items: center;
    justify-content: center;
    background: #fff;
    border-radius: 16px;
    overflow: hidden;

    img {
      width: var(--logo-wall-img-w, 68%);
      height: var(--logo-wall-img-h, 56%);
      object-fit: var(--logo-wall-img-fit, contain);
      display: block;
    }
  }

  @media (prefers-reduced-motion: reduce) {
    &__track {
      animation: none;
      flex-wrap: wrap;
      width: auto;
    }

    &__row {
      overflow: visible;
    }
  }
}
</style>
